import React, { useEffect, useRef, useState, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import { useLocalSearchParams, router } from 'expo-router';
import { Check, MessageCircle, ChevronRight, MapPin, Share2 } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  TYPOGRAPHY,
  ELEVATION,
  CONTENT_MAX_WIDTH,
  DESKTOP_CONTENT_MAX_WIDTH,
  READING_MAX_WIDTH,
} from '../../src/theme';
import { DotLabel } from '@components/common';
import { Header } from '@components/layout';
import { CommentsSheet } from '@components/posts/CommentsSheet';
import { PostDetailBody } from '@components/posts/PostDetailBody';
import { usePostsStore } from '../../src/store/postsStore';
import { useUIStore } from '../../src/store/uiStore';
import { useAuthGate } from '@hooks/useAuthGate';
import { useDesktopShell } from '@hooks/useDesktopShell';
import { sharePost } from '@utils/share';
import { normalizePostCategory } from '../../src/types';

export default function PostDetailScreen() {
  const { id, openComments } = useLocalSearchParams<{ id: string; openComments?: string }>();
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const isDesktopShell = useDesktopShell();
  // Header-only read: PostDetailBody owns the fetch (and the loading/error
  // states) — this just needs enough to pick the header title and the
  // share/show-on-map affordances once the shared cache has the post.
  const selectedPost = usePostsStore((s) => (id ? (s.postsById[id] ?? null) : null));

  const { requireAuth } = useAuthGate();
  const [commentsVisible, setCommentsVisible] = useState(false);

  // Auto-open comments if navigated with openComments param, phone/tablet
  // only — the desktop shell's thread is already in the page, and
  // PostDetailBody handles scrolling it into view itself.
  useEffect(() => {
    if (openComments !== '1' || !selectedPost || isDesktopShell) return;
    setCommentsVisible(true);
  }, [openComments, selectedPost, isDesktopShell]);

  // Mirrors app/structure/[id].tsx's handleShowOnMap. A structure-linked
  // post has no pin of its own (filtered out in useMapSubscriptions.ts) —
  // it surfaces via the structure pin + StructureDetailSheet, so focusing
  // the structure is the correct "view on map" behavior for it. An
  // unlinked located post focuses its own coordinates instead.
  const handleShowOnMap = useCallback(() => {
    if (!selectedPost) return;
    if (selectedPost.structureId) {
      useUIStore.getState().requestMapFocus(selectedPost.structureId);
    } else {
      useUIStore.getState().requestMapPostFocus(selectedPost.id);
    }
    router.dismissTo('/');
  }, [selectedPost]);

  // Desktop browsers usually lack navigator.share, so sharing falls back to
  // the clipboard — confirm it in the icon, the way the structure screen does.
  const [shareCopied, setShareCopied] = useState(false);
  const shareCopiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (shareCopiedTimer.current) clearTimeout(shareCopiedTimer.current);
    },
    []
  );
  const handleShare = useCallback(async () => {
    if (!selectedPost) return;
    const result = await sharePost({ postId: selectedPost.id, caption: selectedPost.caption });
    if (result === 'failed') {
      useUIStore.getState().showToast({ message: "Couldn't copy the link" });
      return;
    }
    if (result !== 'copied') return;
    useUIStore.getState().showToast({ message: 'Link copied' });
    setShareCopied(true);
    if (shareCopiedTimer.current) clearTimeout(shareCopiedTimer.current);
    shareCopiedTimer.current = setTimeout(() => setShareCopied(false), 2000);
  }, [selectedPost]);

  // Contextual header: the address grounds the observation in a place; the
  // category is the fallback when a post isn't tied to an address. Falls
  // back to a generic title until PostDetailBody's fetch lands the post in
  // the shared cache (or if it never does — the not-found/denied states
  // render inside the body, below this same header).
  const postCategory = selectedPost ? normalizePostCategory(selectedPost.category) : null;
  const headerTitle = !selectedPost
    ? 'Post'
    : postCategory === 'news'
      ? selectedPost.title || 'News post'
      : selectedPost.address || (postCategory === 'risk' ? 'Opportunity' : 'Progress observation');

  const canShowOnMap = Boolean(selectedPost?.structureId || selectedPost?.location);

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Icons-only top bar (mockup dethead: share + pin) — the big light
          title below carries the screen instead. Share is docked here, not in
          the card's action row, so it matches the structure screen and doesn't
          appear twice on one screen. */}
      <Header
        showBack
        // A news post reads in the 680px column (READING_MAX_WIDTH), not the
        // 1120px grid width `wide` centers on — the title used to land at
        // the grid's left edge while the body below it sat in the narrower,
        // separately-centered reading column (Sept 29 2026, F1).
        contentMaxWidth={postCategory === 'news' ? READING_MAX_WIDTH : DESKTOP_CONTENT_MAX_WIDTH}
        secondRightIcon={shareCopied ? Check : Share2}
        onSecondRightPress={handleShare}
        secondRightLabel={shareCopied ? 'Link copied' : 'Share post'}
        rightIcon={canShowOnMap ? MapPin : undefined}
        rightLabel={canShowOnMap ? 'Show on map' : undefined}
        onRightPress={canShowOnMap ? handleShowOnMap : undefined}
      />

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View
          style={[
            styles.contentConstraint,
            isDesktopShell && {
              // News is text to read: a reading column, not the full width
              // an observation's photo wants (Sept 28).
              maxWidth: postCategory === 'news' ? READING_MAX_WIDTH : DESKTOP_CONTENT_MAX_WIDTH,
            },
          ]}>
          {postCategory === 'news' && (
            <View style={styles.newsKicker}>
              <DotLabel label="News" variant="news" />
            </View>
          )}
          <Text style={[styles.detailTitle, { color: colors.text }]}>{headerTitle}</Text>
          <PostDetailBody
            postId={id ?? ''}
            openComments={openComments === '1'}
            onRequestComments={() => setCommentsVisible(true)}
          />
        </View>
      </ScrollView>

      {/* Docked comment pill (mockup askbar) — comments are always one tap
          away; the pill opens the same CommentsSheet. In the layout (not
          floated over the scroll) so it can never cover a tappable element:
          Jason caught it resting on the "Add an after photo" button. */}
      {!isDesktopShell && selectedPost && (
        <View style={[styles.commentBarWrap, { paddingBottom: insets.bottom + SPACING.md }]}>
          <TouchableOpacity
            testID="post-comment-bar"
            style={[
              styles.commentBar,
              ELEVATION.soft,
              { backgroundColor: colors.background, borderColor: colors.borderStrong },
            ]}
            onPress={() =>
              requireAuth(() => setCommentsVisible(true), {
                message: 'Sign in to view comments',
                allowAnonymous: true,
              })
            }
            activeOpacity={0.85}
            accessibilityRole="button"
            accessibilityLabel="View comments">
            <MessageCircle size={16} color={colors.textSecondary} />
            <Text style={[styles.commentText, { color: colors.textSecondary }]} numberOfLines={1}>
              {(selectedPost.commentsCount ?? 0) > 0
                ? `Add a comment · ${selectedPost.commentsCount} so far`
                : 'Add a comment'}
            </Text>
            <View style={[styles.commentSend, { backgroundColor: colors.action }]}>
              <ChevronRight size={13} color={colors.actionOn} />
            </View>
          </TouchableOpacity>
        </View>
      )}

      {!isDesktopShell && commentsVisible && selectedPost && (
        <CommentsSheet
          postId={selectedPost.id}
          postAuthorId={selectedPost.authorId}
          commentsCount={selectedPost.commentsCount}
          visible={commentsVisible}
          onClose={() => setCommentsVisible(false)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    alignItems: 'center',
    paddingBottom: SPACING.lg,
  },
  contentConstraint: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
  },
  // The "• News" label above a News post's title, where the observation
  // page's Category row used to say it.
  newsKicker: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
    alignItems: 'flex-start',
  },
  detailTitle: {
    ...TYPOGRAPHY.screenTitle,
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.sm,
  },
  commentBarWrap: {
    alignItems: 'center',
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.sm,
  },
  commentBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.full,
    paddingVertical: SPACING.sm + 2,
    paddingHorizontal: SPACING.md,
  },
  commentText: {
    ...TYPOGRAPHY.bodySmall,
    flex: 1,
  },
  commentSend: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
